import * as THREE from 'three';
import { settings } from '@/config';
import type { QualityTier, Resizable, SizesState } from '@/types';

/**
 * Reusable WebGL renderer wrapper. Centralizes color management, tone mapping,
 * shadows, automatic (clamped) pixel ratio and runtime quality scaling.
 */
export class Renderer implements Resizable {
  readonly instance: THREE.WebGLRenderer;
  readonly maxAnisotropy: number;

  private sizes: SizesState;
  private pixelRatioScale = 1;

  constructor(canvas: HTMLCanvasElement, sizes: SizesState) {
    this.sizes = sizes;
    const cfg = settings.renderer;

    this.instance = new THREE.WebGLRenderer({
      canvas,
      antialias: cfg.antialias,
      alpha: cfg.alpha,
      powerPreference: cfg.powerPreference,
      stencil: cfg.stencil,
      depth: true,
    });

    // Accumulate draw-call stats across ALL of a frame's render calls instead of resetting on
    // each one; `Experience.render` resets exactly once per frame. See the note there.
    this.instance.info.autoReset = false;

    THREE.ColorManagement.enabled = true;
    this.instance.outputColorSpace = cfg.outputColorSpace;
    this.instance.toneMapping = cfg.toneMapping;
    this.instance.toneMappingExposure = cfg.toneMappingExposure;
    this.instance.setClearColor(new THREE.Color(cfg.clearColor), cfg.clearAlpha);

    this.instance.shadowMap.enabled = cfg.shadows.enabled;
    this.instance.shadowMap.type = cfg.shadows.type;
    this.instance.shadowMap.autoUpdate = cfg.shadows.autoUpdate;

    this.maxAnisotropy = this.instance.capabilities.getMaxAnisotropy();
    this.applySize();
  }

  private applySize(): void {
    const { width, height, pixelRatio } = this.sizes;
    // Drawing buffer is sized in device pixels; CSS controls the displayed size.
    this.instance.setSize(width, height, false);
    this.instance.setPixelRatio(
      Math.min(pixelRatio, settings.renderer.maxPixelRatio) * this.pixelRatioScale,
    );
  }

  resize(sizes: SizesState): void {
    this.sizes = sizes;
    this.applySize();
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    this.instance.render(scene, camera);
  }

  /** Pre-compile shaders for the scene to avoid first-frame stutter (pipeline trick). */
  compile(scene: THREE.Scene, camera: THREE.Camera): void {
    this.instance.compile(scene, camera);
  }

  // --- Performance scaling ---------------------------------------------------

  setPixelRatioScale(scale: number): void {
    this.pixelRatioScale = THREE.MathUtils.clamp(scale, 0.1, 1);
    this.applySize();
  }

  getPixelRatioScale(): number {
    return this.pixelRatioScale;
  }

  setShadowsEnabled(enabled: boolean): void {
    this.instance.shadowMap.enabled = enabled;
    this.instance.shadowMap.needsUpdate = true;
  }

  /** Discrete fallback tiers used by the adaptive performance monitor. */
  applyQualityTier(tier: QualityTier): void {
    switch (tier) {
      case 'high':
        this.setPixelRatioScale(1);
        this.setShadowsEnabled(true);
        break;
      case 'medium':
        this.setPixelRatioScale(0.8);
        this.setShadowsEnabled(true);
        break;
      case 'low':
        this.setPixelRatioScale(settings.performance.adaptive.minPixelRatioScale);
        this.setShadowsEnabled(false);
        break;
    }
  }

  get info(): THREE.WebGLRenderer['info'] {
    return this.instance.info;
  }

  dispose(): void {
    this.instance.dispose();
  }
}
